/**
 * useCompetitiveAnalysis Hook
 *
 * A custom React hook that provides competitive pricing analysis functionality for retailers.
 * This hook enables retailers to compare their product prices with nearby competitors,
 * analyze price differences, and make data-driven pricing decisions.
 *
 * Features:
 * - Nearby competitor discovery within a specified radius
 * - Product price comparison across competitors
 * - Advanced filtering and sorting capabilities
 * - Real-time search functionality for products and brands
 *
 * @returns {UseCompetitiveAnalysisT} An object containing state and functions for competitive analysis
 */
import {debounce} from 'lodash';
import {useCallback, useMemo, useState} from 'react';

import {useElasticQuery} from '@/hooks/useElasticQuery';

import {
  CompetitiveAnalysisFilterState,
  CompetitiveAnalysisSortOption,
  ListingSearchResultT,
  ProductPriceComparisonT,
  ProductViewMode,
  UseCompetitiveAnalysisT,
} from '../types/competitive-analysis.types';

// Types
type CompetitorPriceT = {
  listingId: string;
  listingName: string;
  listingAddress: string;
  price: number;
  discountLabel?: string;
};

export type CompetitorLocationT = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  menu_id: string;
  isDelivery: boolean;
};

// Default values
const defaultSortOption: CompetitiveAnalysisSortOption = {
  label: 'Product Name (A-Z)',
  field: 'productName',
  direction: 'asc',
};

const defaultFilterState: CompetitiveAnalysisFilterState = {
  searchInput: '',
  brandSearchInput: '',
  priceRange: {min: 0, max: 1000},
  priceDifferenceRange: {min: -100, max: 100},
  competitorCountRange: {min: 0, max: 100},
};

/**
 * Helper function to calculate competitor price stats
 */
const calculateCompetitorPrices = (competitors: CompetitorPriceT[], currentPrice: number) => {
  if (competitors.length === 0) {
    return {
      min: currentPrice,
      max: currentPrice,
      avg: currentPrice,
      competitors,
    };
  }

  const min = Math.min(...competitors.map(c => c.price));
  const max = Math.max(...competitors.map(c => c.price));
  const avg = competitors.reduce((sum, comp) => sum + comp.price, 0) / competitors.length;

  return {
    min,
    max,
    avg,
    competitors,
  };
};

/**
 * Helper function to apply filters to products
 */
const applyFilters = (
  products: ProductPriceComparisonT[],
  filterState: CompetitiveAnalysisFilterState
): ProductPriceComparisonT[] => {
  return products.filter(product => {
    // Price range filter
    const priceInRange =
      product.currentPrice >= filterState.priceRange.min && product.currentPrice <= filterState.priceRange.max;

    // Price difference filter
    const priceDiff = ((product.currentPrice - product.competitorPrices.avg) / product.competitorPrices.avg) * 100;
    const priceDiffInRange =
      priceDiff >= filterState.priceDifferenceRange.min && priceDiff <= filterState.priceDifferenceRange.max;

    // Competitor count filter
    const competitorCountInRange =
      product.competitorPrices.competitors.length >= filterState.competitorCountRange.min &&
      product.competitorPrices.competitors.length <= filterState.competitorCountRange.max;

    return priceInRange && priceDiffInRange && competitorCountInRange;
  });
};

/**
 * Helper function to sort products
 */
const sortProducts = (
  products: ProductPriceComparisonT[],
  sortOption: CompetitiveAnalysisSortOption
): ProductPriceComparisonT[] => {
  return [...products].sort((a, b) => {
    let comparison = 0;
    let diffA: number, diffB: number;

    switch (sortOption.field) {
      case 'productName':
        comparison = a.productName.localeCompare(b.productName);
        break;
      case 'currentPrice':
        comparison = a.currentPrice - b.currentPrice;
        break;
      case 'priceDifference':
        diffA = ((a.currentPrice - a.competitorPrices.avg) / a.competitorPrices.avg) * 100;
        diffB = ((b.currentPrice - b.competitorPrices.avg) / b.competitorPrices.avg) * 100;
        comparison = diffA - diffB;
        break;
      case 'competitorCount':
        comparison = a.competitorPrices.competitors.length - b.competitorPrices.competitors.length;
        break;
      default:
        comparison = 0;
    }

    return sortOption.direction === 'asc' ? comparison : -comparison;
  });
};

/**
 * Helper function to process products and their competitors
 */
const processProducts = (
  sourceProducts: any[],
  competitorData: any[],
  isVerified: boolean
): ProductPriceComparisonT[] => {
  const productMap = new Map<string, ProductPriceComparisonT>();

  // Process source products
  sourceProducts.forEach((product: any) => {
    // Skip products that don't match our verification status
    if (isVerified === !product._source.brand_endorsement) return;

    const key = isVerified ? product._source.brand_endorsement.product_id : product._source.name;
    productMap.set(key, {
      productId: isVerified ? product._source.brand_endorsement.product_id : product._source.id,
      productName: product._source.name,
      productLogo: product._source.avatar_image?.original_url || '',
      brand: {
        brandName: isVerified ? product._source.brand_endorsement.brand_name : 'Unverified',
        brandLogo: isVerified ? product._source.brand_endorsement.brand_avatar_image_url : '',
      },
      originalPrice: product._source.price.original_price,
      currentPrice: product._source.price.price,
      discountLabel: product._source.price.discount_label,
      competitorPrices: {
        min: Infinity,
        max: -Infinity,
        avg: 0,
        competitors: [],
      },
    });
  });

  // Process competitor prices
  competitorData.forEach((product: any) => {
    const existingProduct = productMap.get(product.key);

    if (existingProduct) {
      // map over product.listings_count.buckets and then the competitors are these listings
      const competitors = product.listings_count.buckets.map((comp: any) => {
        return {
          listingId: comp.listing_details.hits.hits[0]._source.id,
          listingName: comp.listing_details.hits.hits[0]._source.listings.name,
          listingAddress:
            comp.listing_details.hits.hits[0]._source.listings.address ||
            comp.listing_details.hits.hits[0]._source.listings.city,
          price: comp.listing_details.hits.hits[0]._source.price.price,
          discountLabel: comp.listing_details.hits.hits[0]._source.price.discount_label,
        };
      });

      existingProduct.competitorPrices.competitors = competitors;
    }
  });

  // Calculate price stats
  for (const product of productMap.values()) {
    product.competitorPrices = calculateCompetitorPrices(product.competitorPrices.competitors, product.currentPrice);
  }

  return Array.from(productMap.values());
};

export function useCompetitiveAnalysis(): UseCompetitiveAnalysisT {
  // State Management
  const [selectedListing, setSelectedListing] = useState<ListingSearchResultT | null>(null);
  const [searchRadius, setSearchRadius] = useState(5); // Default 10 miles
  const [sortOption, setSortOption] = useState<CompetitiveAnalysisSortOption>(defaultSortOption);
  const [filterState, setFilterState] = useState<CompetitiveAnalysisFilterState>(defaultFilterState);
  const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
  const [debouncedBrandSearchInput, setDebouncedBrandSearchInput] = useState('');
  const [productViewMode, setProductViewMode] = useState<ProductViewMode>('verified');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Debounced search handlers
  const debouncedSetSearchInput = useCallback(
    debounce((value: string) => setDebouncedSearchInput(value), 500),
    []
  );

  const debouncedSetBrandSearchInput = useCallback(
    debounce((value: string) => setDebouncedBrandSearchInput(value), 500),
    []
  );

  /**
   * Updates filter state with debounced search inputs
   * This prevents excessive API calls while typing
   */
  const setFilterStateWithDebounce = useCallback(
    (newState: CompetitiveAnalysisFilterState) => {
      setFilterState(newState);
      if (newState.searchInput !== filterState.searchInput) {
        debouncedSetSearchInput(newState.searchInput);
      }
      if (newState.brandSearchInput !== filterState.brandSearchInput) {
        debouncedSetBrandSearchInput(newState.brandSearchInput);
      }
    },
    [filterState.searchInput, filterState.brandSearchInput, debouncedSetSearchInput, debouncedSetBrandSearchInput]
  );

  // Data Fetching: Nearby Listings
  const {
    data: nearbyListingsData,
    isLoading: nearbyListingsLoading,
    error: nearbyListingsError,
  } = useElasticQuery({
    table: 'tnc.weedmaps.listings',
    searchRequest: {
      index: 'tnc.weedmaps.listings',
      size: 10000,
      query: {
        bool: {
          must: selectedListing
            ? [
                {
                  geo_distance: {
                    distance: `${searchRadius}mi`,
                    coordinates: {
                      lat: selectedListing.latitude,
                      lon: selectedListing.longitude,
                    },
                  },
                },
              ]
            : [],
        },
      },
      sort: selectedListing
        ? [
            {
              _geo_distance: {
                coordinates: {
                  lat: selectedListing.latitude,
                  lon: selectedListing.longitude,
                },
                order: 'asc',
                unit: 'mi',
                mode: 'min',
                distance_type: 'arc',
              },
            },
          ]
        : undefined,
      collapse: {
        field: 'menu_id',
      },
      _source: ['id', 'name', 'menu_id', 'latitude', 'longitude', 'online_ordering'],
    },
    enabled: !!selectedListing,
  });

  // Transform nearby listings into competitor locations
  const competitorLocations = useMemo(() => {
    if (!nearbyListingsData?.data) return [];
    return nearbyListingsData.data
      .map((hit: any) => ({
        id: hit._source.id,
        name: hit._source.name,
        latitude: hit._source.latitude,
        longitude: hit._source.longitude,
        menu_id: hit._source.menu_id,
        isDelivery: hit._source.online_ordering.enabled_for_delivery ? true : false,
      }))
      .filter((competitor: CompetitorLocationT) => competitor.menu_id !== selectedListing?.menu_id);
  }, [nearbyListingsData, selectedListing?.menu_id]);

  // Extract menu IDs from nearby listings
  const nearbyMenuIds = useMemo(() => {
    if (!nearbyListingsData?.data) return [];
    return nearbyListingsData.data.map((hit: any) => hit._source.menu_id);
  }, [nearbyListingsData]);

  // Data Fetching: Selected Listing's Products
  const {
    data: selectedListingProducts,
    isLoading: selectedProductsLoading,
    error: selectedProductsError,
    isFetching: selectedProductsFetching,
    pagination: selectedListingPagination,
  } = useElasticQuery({
    table: 'tnc.weedmaps.menus',
    searchRequest: {
      size: itemsPerPage,

      index: 'tnc.weedmaps.menus',
      query: {
        bool: {
          filter: [
            {
              range: {
                __ts: {
                  // gte: 'now-7d/d',
                  // 48 hours
                  gte: 'now-7d/d',
                  lte: 'now',
                },
              },
            },
          ],
          must: [
            {
              term: {
                menu_id: selectedListing?.menu_id,
              },
            },
            ...(debouncedSearchInput
              ? [
                  {
                    match_phrase_prefix: {
                      name: debouncedSearchInput,
                    },
                  },
                ]
              : []),
            ...(debouncedBrandSearchInput
              ? [
                  {
                    match_phrase_prefix: {
                      'brand_endorsement.brand_name': debouncedBrandSearchInput,
                    },
                  },
                ]
              : []),
          ],
        },
      },
      _source: [
        'id',
        'name',
        'price',
        'menu_id',
        'category',
        'brand_endorsement',
        'brand_endorsement.brand_logo',
        'avatar_image',
      ],
    },
    enabled: !!selectedListing,
    page: currentPage,
    limit: itemsPerPage,
  });

  console.log('SELECTED LISTING SPRODUCSTS HERE', selectedListingProducts);

  // Data Fetching: Competitor Pricing Data
  const filteredMenuIds = nearbyMenuIds.filter(id => id !== selectedListing?.menu_id);
  const updatedProductIds =
    selectedListingProducts?.data
      ?.filter((p: any) => p._source.brand_endorsement)
      .map((p: any) => p._source.brand_endorsement.product_id) || [];
  const unverifiedListingsProducts = selectedListingProducts?.data?.filter((p: any) => !p._source.brand_endorsement);
  const unverifiedProductNames = unverifiedListingsProducts?.map((p: any) => p._source.name);

  const {
    data: localPricingProductData,
    isLoading: localPricingLoading,
    error: localPricingError,
    isFetching: localPricingFetching,
  } = useElasticQuery({
    table: 'tnc.weedmaps.menus',
    searchRequest: {
      index: 'tnc.weedmaps.menus',
      size: 10000,
      _source: ['id', 'name', 'price', 'menu_id', 'category', 'brand_endorsement', 'listings'],
      query: {
        bool: {
          filter: [
            {
              range: {
                __ts: {
                  // gte: 'now-7d/d',
                  // 48 hours
                  gte: 'now-7d/d',
                  lte: 'now',
                },
              },
            },
          ],
          must: [
            {
              terms: {
                'brand_endorsement.product_id': updatedProductIds as (string | number)[],
              },
            },
            {
              terms: {
                'listings.menu_id': filteredMenuIds,
              },
            },
          ],
        },
      },
      collapse: {
        field: 'menu_id',
      },
    },
    aggs: {
      by_product: {
        terms: {
          field: 'brand_endorsement.product_id',
          size: 10000,
        },
        aggs: {
          price_stats: {
            stats: {
              field: 'price.price',
            },
          },
          listings_count: {
            terms: {
              field: 'menu_id',
              size: 10000,
            },
            aggs: {
              listing_details: {
                top_hits: {
                  size: 1,
                  _source: ['id', 'name', 'price', 'menu_id', 'listings'],
                },
              },
            },
          },
        },
      },
    },
    enabled: !!selectedListingProducts?.data?.length && !!selectedListing,
  });

  console.log('LOCAL PRICING PRODUCT DATA', localPricingProductData);
  console.log('UNVERIFIED PRODUCT NAMES', unverifiedProductNames);

  const {
    data: localPricingUnverifiedProductData,
    isLoading: localPricingUnverifiedProductLoading,
    error: localPricingUnverifiedProductError,
    isFetching: localPricingUnverifiedProductFetching,
  } = useElasticQuery({
    table: 'tnc.weedmaps.menus',
    searchRequest: {
      index: 'tnc.weedmaps.menus',
      size: 10000,
      _source: ['id', 'name', 'price', 'menu_id', 'category', 'brand_endorsement', 'listings'],
      query: {
        bool: {
          filter: [
            {
              range: {
                __ts: {
                  gte: 'now-7d/d',
                  lte: 'now',
                },
              },
            },
          ],
          must: [
            {
              bool: {
                should: (unverifiedProductNames || []).map(name => ({
                  match_phrase: {
                    name: name,
                  },
                })),
                minimum_should_match: 1,
              },
            },
            {
              terms: {
                'listings.menu_id': filteredMenuIds,
              },
            },
          ],
        },
      },
      collapse: {
        field: 'menu_id',
      },
    },
    aggs: {
      by_product: {
        terms: {
          field: 'brand_endorsement.product_id',
          size: 10000,
        },
        aggs: {
          price_stats: {
            stats: {
              field: 'price.price',
            },
          },
          listings_count: {
            terms: {
              field: 'menu_id',
              size: 10000,
            },
            aggs: {
              listing_details: {
                top_hits: {
                  size: 1,
                  _source: ['id', 'name', 'price', 'menu_id', 'listings'],
                },
              },
            },
          },
        },
      },
    },
    // enabled: unverifiedProductNames && unverifiedProductNames.length > 0 && !!selectedListingProducts?.data?.length,
    enabled: false,
  });

  console.log('LOCAL PRICING UNVERIFIED PRODUCT DATA', localPricingUnverifiedProductData);

  // TODO: if no selected listings then just products = []
  // Process verified products
  const products = useMemo(
    () =>
      processProducts(
        selectedListingProducts?.data || [],
        localPricingProductData?.aggregations?.by_product?.buckets || [],
        true
      ),
    [selectedListingProducts?.data, localPricingProductData?.aggregations]
  );

  // Process unverified products
  const unverifiedProducts = useMemo(
    () =>
      processProducts(
        selectedListingProducts?.data || [],
        localPricingUnverifiedProductData?.aggregations?.by_product?.buckets || [],
        false
      ),
    [selectedListingProducts?.data, localPricingUnverifiedProductData?.aggregations]
  );

  // Filter and sort verified products
  const filteredAndSortedProducts = useMemo(
    () => sortProducts(applyFilters(products, filterState), sortOption),
    [products, filterState, sortOption]
  );

  // Filter and sort unverified products
  const filteredAndSortedUnverifiedProducts = useMemo(
    () => sortProducts(applyFilters(unverifiedProducts, filterState), sortOption),
    [unverifiedProducts, filterState, sortOption]
  );

  // Calculate displayed products based on view mode
  const displayedProducts = useMemo(() => {
    switch (productViewMode) {
      case 'verified':
        return filteredAndSortedProducts;
      case 'unverified':
        return filteredAndSortedUnverifiedProducts;
      case 'all':
        return sortProducts(applyFilters([...products, ...unverifiedProducts], filterState), sortOption);
      default:
        return filteredAndSortedProducts;
    }
  }, [
    productViewMode,
    filteredAndSortedProducts,
    filteredAndSortedUnverifiedProducts,
    products,
    unverifiedProducts,
    filterState,
    sortOption,
  ]);

  // Loading and error states
  const isLoading =
    nearbyListingsLoading ||
    selectedProductsLoading ||
    localPricingLoading ||
    selectedProductsFetching ||
    localPricingFetching ||
    localPricingUnverifiedProductLoading ||
    localPricingUnverifiedProductFetching;
  const error = nearbyListingsError || selectedProductsError || localPricingError || localPricingUnverifiedProductError;

  return {
    selectedListing,
    searchRadius,
    setSelectedListing,
    setSearchRadius,
    products,
    unverifiedProducts,
    productViewMode,
    setProductViewMode,
    isLoading,
    error,
    sortOption,
    setSortOption,
    filterState,
    setFilterState: setFilterStateWithDebounce,
    filteredAndSortedProducts,
    filteredAndSortedUnverifiedProducts,
    displayedProducts,
    competitorLocations,
    pagination: selectedListingPagination,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
  };
}
